// The app's body face is viewport-conditional: below the desktop breakpoint
// --wp-font-sans switches to a condensed family (see the @media block in
// assets/css/tokens.scss). Canvas surfaces — Chart.js — cannot read a CSS custom
// property, so they have to be handed the current stack in JS.
//
// Read the resolved token rather than restating the families here. That keeps the
// breakpoint, the leading family and the 20-entry script-fallback chain in exactly
// one place; this module never needs to know any of the three.

// Last resort only, for the window before stylesheets are applied. Not a source of
// truth — the token is. Kept deliberately short since it can never render anything
// the real chain wouldn't have.
const UNRESOLVED_FALLBACK =
  "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

export const readSansFontFamily = () => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--wp-font-sans')
    // The token is authored across several lines; the canvas font shorthand does
    // not parse a value containing newlines.
    .replace(/\s+/g, ' ')
    .trim();

  return value || UNRESOLVED_FALLBACK;
};

// Calls back with the new stack whenever the resolved token changes.
//
// Driven by resize rather than a matchMedia on the breakpoint, so there is no
// breakpoint constant here to drift out of sync with tokens.scss — move the @media
// block and this keeps working untouched. The read is throttled to one per frame and
// the handler only fires when the value actually changed, so a drag-resize costs one
// custom-property read per frame and nothing else.
//
// Returns an unsubscribe function.
export const onSansFontChange = (handler) => {
  let current = readSansFontFamily();
  let frame = 0;

  const check = () => {
    frame = 0;
    const next = readSansFontFamily();
    if (next === current) return;
    current = next;
    handler(next);
  };

  const onResize = () => {
    if (frame) return;
    frame = requestAnimationFrame(check);
  };

  window.addEventListener('resize', onResize);

  return () => {
    window.removeEventListener('resize', onResize);
    if (frame) cancelAnimationFrame(frame);
  };
};
